import { useState } from 'react';
import { Alert, Flex, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCloseDocumentWithoutSignature } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useCloseDocumentWithoutSignature';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

const REASON_MAX_LENGTH = 300;

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	opened: boolean;
	onClose: () => void;
}

export const CloseDocumentModal = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	opened,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [reason, setReason] = useState('');
	const handleClose = () => {
		setReason('');
		onClose();
	};
	const { close, isPending } = useCloseDocumentWithoutSignature({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
		onSuccess: handleClose,
	});

	const canSubmit = reason.trim().length > 0 && !isPending;

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			closeOnClickOutside={false}
			size="lg"
			padding="md"
			radius="md"
			withCloseButton
			centered
			title={
				<Text size="18px" fw={600}>
					{tMipManage('documents_page.close_document.modal.title')}
				</Text>
			}
			styles={{
				body: { backgroundColor: '#fff' },
				header: { backgroundColor: '#fff', marginBottom: 0 },
				content: { backgroundColor: '#fff' },
			}}
		>
			<Stack gap="sm">
				<Stack gap={6}>
					<Flex justify="space-between" align="center">
						<Text size="14px" fw={600}>
							{tMipManage('documents_page.close_document.modal.reason_label')}
						</Text>
						<Text size="11px" c="gray.7">
							{reason.length}/{REASON_MAX_LENGTH}
						</Text>
					</Flex>
					<Textarea
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						placeholder={tMipManage(
							'documents_page.close_document.modal.reason_placeholder'
						)}
						maxLength={REASON_MAX_LENGTH}
						minRows={3}
						autosize
						data-testid={TEST_ELEMENT_IDS.CLOSE_DOCUMENT_REASON_INPUT}
					/>
				</Stack>

				<Alert
					color="blue"
					variant="light"
					radius="sm"
					icon={<Icon variant="information" size="lg" />}
				>
					<Text size="13px" c="gray.8">
						{tMipManage('documents_page.close_document.modal.warning')}
					</Text>
				</Alert>

				<Group justify="right" gap="xs">
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="tertiary"
						onClick={handleClose}
						disabled={isPending}
					>
						{tMipManage('documents_page.close_document.modal.cancel')}
					</ButtonV2>
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="primary"
						disabled={!canSubmit}
						onClick={() => close(reason.trim())}
						data-testid={TEST_ELEMENT_IDS.CLOSE_DOCUMENT_CONFIRM_BUTTON}
					>
						{tMipManage('documents_page.close_document.modal.confirm')}
					</ButtonV2>
				</Group>
			</Stack>
		</Modal>
	);
};
